import type {
  SpreadsheetRangeAddress,
  SpreadsheetSparklineAxisMode,
  SpreadsheetSparklineEmptyCellPolicy,
  SpreadsheetSparklineGroup,
  SpreadsheetSparklinePlotBy,
  SpreadsheetSparklineType,
} from '../protocol/spreadsheetEngineProtocol'
import { formatSelectionAddress, parseA1Selection } from './addressing'

export type ParsedSparklineRangeDraft = {
  range: SpreadsheetRangeAddress
  formula: string
}

export type SparklineDialogGroupDraft = {
  type: SpreadsheetSparklineType
  plotBy: SpreadsheetSparklinePlotBy
  dataRangeDraft: string
  locationRangeDraft: string
  dateAxis: boolean
  dateRangeDraft: string
  emptyPolicy: SpreadsheetSparklineEmptyCellPolicy
  displayHidden: boolean
  displayXAxis: boolean
  rightToLeft: boolean
  minAxis: SpreadsheetSparklineAxisMode
  maxAxis: SpreadsheetSparklineAxisMode
  manualMin: string
  manualMax: string
  lineWeight: string
  seriesColor: string
  negativeColor: string
  axisColor: string
  markerColor: string
  markers: boolean
  highPoint: boolean
  lowPoint: boolean
  firstPoint: boolean
  lastPoint: boolean
  negativePoints: boolean
}

export type SparklineGroupBuildResult =
  | { ok: true; group: SpreadsheetSparklineGroup; groupId: string }
  | { ok: false; error: string }

export type SparklineDialogApplyPlan =
  | { action: 'upsert-group'; group: SpreadsheetSparklineGroup; groupId: string }
  | { action: 'blocked'; error: string }

export type SparklineDialogCommandPlan =
  | { action: 'clear-selection' }
  | { action: 'delete-group'; groupId: string; clearEditingGroup: boolean }
  | { action: 'group-locations'; groupId: string; locations: SpreadsheetRangeAddress[] }
  | { action: 'ungroup-group'; groupId: string; clearEditingGroup: boolean }
  | { action: 'noop' }

export const SPARKLINE_WORKSHEET_SIZE = { colCount: 16_384, rowCount: 1_048_576 }

export const SPARKLINE_TYPE_OPTIONS: Array<{ value: SpreadsheetSparklineType; label: string }> = [
  { value: 'line', label: 'Line' },
  { value: 'column', label: 'Column' },
  { value: 'winLoss', label: 'Win/Loss' },
]

export const SPARKLINE_PLOT_BY_OPTIONS: Array<{ value: SpreadsheetSparklinePlotBy; label: string }> = [
  { value: 'rows', label: 'Rows' },
  { value: 'columns', label: 'Columns' },
]

export const SPARKLINE_EMPTY_CELL_POLICY_OPTIONS: Array<{ value: SpreadsheetSparklineEmptyCellPolicy; label: string }> = [
  { value: 'gap', label: 'Gaps' },
  { value: 'zero', label: 'Zero' },
  { value: 'span', label: 'Connect points' },
]

export const SPARKLINE_AXIS_MODE_OPTIONS: Array<{ value: SpreadsheetSparklineAxisMode; label: string }> = [
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Same for group' },
  { value: 'custom', label: 'Custom' },
]

const DEFAULT_COLORS = {
  series: '#107c41',
  negative: '#c2410c',
  axis: '#6b7280',
  marker: '#107c41',
}

export function parseSparklineRangeDraft(input: string, fallbackSheetId: string): ParsedSparklineRangeDraft | null {
  const text = String(input || '').trim().replace(/^=/, '')
  if (!text) return null
  const bangIndex = text.lastIndexOf('!')
  const sheetToken = bangIndex >= 0 ? text.slice(0, bangIndex).trim() : ''
  const addressToken = bangIndex >= 0 ? text.slice(bangIndex + 1).trim() : text
  const selection = parseA1Selection(addressToken, SPARKLINE_WORKSHEET_SIZE)
  if (!selection) return null
  const sheetId = sheetToken
    ? sheetToken.replace(/^'/, '').replace(/'$/, '').replace(/''/g, "'")
    : fallbackSheetId
  const range = {
    sheet_id: sheetId,
    start_row: Math.min(selection.row, selection.endRow),
    start_col: Math.min(selection.col, selection.endCol),
    end_row: Math.max(selection.row, selection.endRow),
    end_col: Math.max(selection.col, selection.endCol),
  }
  return { range, formula: formatSparklineRangeFormula(range, fallbackSheetId) }
}

export function formatSparklineRangeFormula(range: SpreadsheetRangeAddress, fallbackSheetId: string): string {
  const address = formatSelectionAddress({
    col: range.start_col,
    row: range.start_row,
    endCol: range.end_col,
    endRow: range.end_row,
  }, SPARKLINE_WORKSHEET_SIZE)
  return range.sheet_id === fallbackSheetId ? address : `${range.sheet_id}!${address}`
}

export function sparklineRangeFromCell(sheetId: string, row: number, col: number): SpreadsheetRangeAddress {
  return {
    sheet_id: sheetId,
    start_row: row,
    start_col: col,
    end_row: row,
    end_col: col,
  }
}

export function sparklineCellsInRange(range: SpreadsheetRangeAddress): SpreadsheetRangeAddress[] {
  const cells: SpreadsheetRangeAddress[] = []
  for (let row = range.start_row; row <= range.end_row; row += 1) {
    for (let col = range.start_col; col <= range.end_col; col += 1) {
      cells.push(sparklineRangeFromCell(range.sheet_id, row, col))
    }
  }
  return cells
}

export function sparklineSourceSeriesCount(range: SpreadsheetRangeAddress, plotBy: SpreadsheetSparklinePlotBy): number {
  return plotBy === 'columns'
    ? Math.max(1, range.end_col - range.start_col + 1)
    : Math.max(1, range.end_row - range.start_row + 1)
}

export function sparklineSourceSlice(
  range: SpreadsheetRangeAddress,
  plotBy: SpreadsheetSparklinePlotBy,
  index: number,
): SpreadsheetRangeAddress {
  if (plotBy === 'columns') {
    const col = Math.min(range.end_col, range.start_col + index)
    return {
      sheet_id: range.sheet_id,
      start_row: range.start_row,
      start_col: col,
      end_row: range.end_row,
      end_col: col,
    }
  }
  const row = Math.min(range.end_row, range.start_row + index)
  return {
    sheet_id: range.sheet_id,
    start_row: row,
    start_col: range.start_col,
    end_row: row,
    end_col: range.end_col,
  }
}

export function sparklineRangesOverlap(left: SpreadsheetRangeAddress, right: SpreadsheetRangeAddress): boolean {
  return String(left.sheet_id).toLowerCase() === String(right.sheet_id).toLowerCase()
    && left.start_col <= right.end_col
    && left.end_col >= right.start_col
    && left.start_row <= right.end_row
    && left.end_row >= right.start_row
}

export function boundingSparklineRange(
  ranges: SpreadsheetRangeAddress[],
  fallback: SpreadsheetRangeAddress,
): SpreadsheetRangeAddress {
  const sameSheet = ranges.filter((range) => range.sheet_id === ranges[0]?.sheet_id)
  if (sameSheet.length === 0) return fallback
  return {
    sheet_id: sameSheet[0]!.sheet_id,
    start_row: Math.min(...sameSheet.map((range) => range.start_row)),
    start_col: Math.min(...sameSheet.map((range) => range.start_col)),
    end_row: Math.max(...sameSheet.map((range) => range.end_row)),
    end_col: Math.max(...sameSheet.map((range) => range.end_col)),
  }
}

export function parseSparklineNumber(input: string): number | null {
  const text = String(input || '').trim()
  if (!text) return null
  const value = Number(text)
  return Number.isFinite(value) ? value : null
}

export function toSparklineColorInputValue(value: unknown, fallback: string): string {
  const text = String(value || '').trim()
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback
}

export function buildSparklineGroupId(
  sheetId: string,
  type: SpreadsheetSparklineType,
  location: SpreadsheetRangeAddress,
  nowMs = Date.now(),
): string {
  return `sparkline_${sheetId}_${type}_${location.start_row}_${location.start_col}_${nowMs.toString(36)}`
    .replace(/[^a-z0-9_]+/gi, '_')
    .toLowerCase()
}

export function defaultSparklineLocationForSelection(selectedRange: SpreadsheetRangeAddress): SpreadsheetRangeAddress {
  return {
    sheet_id: selectedRange.sheet_id,
    start_row: selectedRange.start_row,
    start_col: selectedRange.end_col + 1,
    end_row: selectedRange.end_row,
    end_col: selectedRange.end_col + 1,
  }
}

export function filterVisibleSparklineGroups(
  groups: SpreadsheetSparklineGroup[],
  selectedRange: SpreadsheetRangeAddress,
): SpreadsheetSparklineGroup[] {
  return groups.filter((group) => (
    String(group.sheet_id).toLowerCase() === selectedRange.sheet_id.toLowerCase()
    && group.sparklines.some((sparkline) => sparklineRangesOverlap(sparkline.location, selectedRange))
  ))
}

export function findSparklineGroupOverlappingRange(
  groups: SpreadsheetSparklineGroup[],
  selectedRange: SpreadsheetRangeAddress,
): SpreadsheetSparklineGroup | null {
  return filterVisibleSparklineGroups(groups, selectedRange)[0] ?? null
}

export function buildSparklineGroupDraftFromGroup(
  group: SpreadsheetSparklineGroup,
  selectedRange: SpreadsheetRangeAddress,
): SparklineDialogGroupDraft {
  const colors = group.colors ?? {}
  const locationRange = boundingSparklineRange(group.sparklines.map((sparkline) => sparkline.location), selectedRange)
  const sourceRanges = group.sparklines
    .map((sparkline) => sparkline.source_range)
    .filter((range): range is SpreadsheetRangeAddress => Boolean(range))
  const firstDateRange = group.sparklines.find((sparkline) => sparkline.date_range)?.date_range
  return {
    type: group.type,
    plotBy: group.plot_by,
    dataRangeDraft: sourceRanges.length > 0
      ? formatSparklineRangeFormula(boundingSparklineRange(sourceRanges, selectedRange), selectedRange.sheet_id)
      : String(group.sparklines[0]?.source_formula ?? ''),
    locationRangeDraft: formatSparklineRangeFormula(locationRange, selectedRange.sheet_id),
    dateAxis: group.date_axis === true,
    dateRangeDraft: firstDateRange
      ? formatSparklineRangeFormula(firstDateRange, selectedRange.sheet_id)
      : String(group.sparklines[0]?.date_range_formula ?? ''),
    emptyPolicy: group.display_empty_cells_as ?? 'gap',
    displayHidden: group.display_hidden !== false,
    displayXAxis: group.display_x_axis === true,
    rightToLeft: group.right_to_left === true,
    minAxis: group.min_axis ?? 'individual',
    maxAxis: group.max_axis ?? 'individual',
    manualMin: group.manual_min == null ? '' : String(group.manual_min),
    manualMax: group.manual_max == null ? '' : String(group.manual_max),
    lineWeight: group.line_weight == null ? '1.25' : String(group.line_weight),
    seriesColor: toSparklineColorInputValue(colors.series, DEFAULT_COLORS.series),
    negativeColor: toSparklineColorInputValue(colors.negative, DEFAULT_COLORS.negative),
    axisColor: toSparklineColorInputValue(colors.axis, DEFAULT_COLORS.axis),
    markerColor: toSparklineColorInputValue(colors.markers, DEFAULT_COLORS.marker),
    markers: group.markers === true,
    highPoint: group.high_point === true,
    lowPoint: group.low_point === true,
    firstPoint: group.first_point === true,
    lastPoint: group.last_point === true,
    negativePoints: group.negative_points !== false,
  }
}

export function buildSparklineGroupFromDraft(args: {
  selectedRange: SpreadsheetRangeAddress
  editingGroupId: string | null
  draft: SparklineDialogGroupDraft
  nowMs?: number
}): SparklineGroupBuildResult {
  const source = parseSparklineRangeDraft(args.draft.dataRangeDraft, args.selectedRange.sheet_id)
  const locations = parseSparklineRangeDraft(args.draft.locationRangeDraft, args.selectedRange.sheet_id)
  if (!source) {
    return { ok: false, error: 'Enter a valid data range, such as A1:F1 or Sheet2!A1:A12.' }
  }
  if (!locations) {
    return { ok: false, error: 'Enter a valid one-cell or multi-cell location range.' }
  }
  const locationCells = sparklineCellsInRange(locations.range)
  const seriesCount = locationCells.length === 1 ? 1 : sparklineSourceSeriesCount(source.range, args.draft.plotBy)
  if (locationCells.length !== seriesCount) {
    return {
      ok: false,
      error: `Location range must contain ${seriesCount} cell${seriesCount === 1 ? '' : 's'} for the selected plot-by direction.`,
    }
  }
  const parsedDateRange = args.draft.dateAxis && args.draft.dateRangeDraft.trim()
    ? parseSparklineRangeDraft(args.draft.dateRangeDraft, args.selectedRange.sheet_id)
    : null
  if (args.draft.dateAxis && args.draft.dateRangeDraft.trim() && !parsedDateRange) {
    return { ok: false, error: 'Enter a valid date axis range or clear the date range field.' }
  }
  const groupId = args.editingGroupId
    ?? buildSparklineGroupId(args.selectedRange.sheet_id, args.draft.type, locations.range, args.nowMs)
  const sparklines = locationCells.map((location, index) => {
    const sourceRange = seriesCount === 1 ? source.range : sparklineSourceSlice(source.range, args.draft.plotBy, index)
    return {
      location,
      source_formula: formatSparklineRangeFormula(sourceRange, args.selectedRange.sheet_id),
      source_range: sourceRange,
      ...(parsedDateRange ? {
        date_range_formula: parsedDateRange.formula,
        date_range: parsedDateRange.range,
      } : null),
    }
  })
  return {
    ok: true,
    groupId,
    group: {
      id: groupId,
      sheet_id: args.selectedRange.sheet_id,
      type: args.draft.type,
      plot_by: args.draft.plotBy,
      sparklines,
      date_axis: args.draft.dateAxis,
      display_empty_cells_as: args.draft.emptyPolicy,
      display_hidden: args.draft.displayHidden,
      display_x_axis: args.draft.displayXAxis,
      right_to_left: args.draft.rightToLeft,
      min_axis: args.draft.minAxis,
      max_axis: args.draft.maxAxis,
      manual_min: parseSparklineNumber(args.draft.manualMin),
      manual_max: parseSparklineNumber(args.draft.manualMax),
      line_weight: Math.max(0.25, parseSparklineNumber(args.draft.lineWeight) ?? 1.25),
      markers: args.draft.markers,
      high_point: args.draft.highPoint,
      low_point: args.draft.lowPoint,
      first_point: args.draft.firstPoint,
      last_point: args.draft.lastPoint,
      negative_points: args.draft.negativePoints,
      colors: {
        series: args.draft.seriesColor,
        negative: args.draft.negativeColor,
        axis: args.draft.axisColor,
        markers: args.draft.markerColor,
        first: args.draft.markerColor,
        last: args.draft.markerColor,
        high: args.draft.markerColor,
        low: args.draft.markerColor,
      },
    },
  }
}

export function buildSparklineDialogApplyPlan(args: {
  selectedRange: SpreadsheetRangeAddress
  editingGroupId: string | null
  draft: SparklineDialogGroupDraft
  nowMs?: number
}): SparklineDialogApplyPlan {
  const result = buildSparklineGroupFromDraft(args)
  if (!result.ok) return { action: 'blocked', error: result.error }
  return { action: 'upsert-group', group: result.group, groupId: result.groupId }
}

export function buildSparklineDialogClearSelectionPlan(): SparklineDialogCommandPlan {
  return { action: 'clear-selection' }
}

export function buildSparklineDialogDeleteGroupPlan(args: {
  selectedGroupId: string | null | undefined
  editingGroupId: string | null | undefined
}): SparklineDialogCommandPlan {
  const groupId = String(args.selectedGroupId || '').trim()
  if (!groupId) return { action: 'noop' }
  return {
    action: 'delete-group',
    groupId,
    clearEditingGroup: args.editingGroupId === groupId,
  }
}

export function buildSparklineDialogGroupLocationsPlan(args: {
  selectedGroupId: string | null | undefined
  selectedRange: SpreadsheetRangeAddress
}): SparklineDialogCommandPlan {
  const groupId = String(args.selectedGroupId || '').trim()
  if (!groupId) return { action: 'noop' }
  return { action: 'group-locations', groupId, locations: [args.selectedRange] }
}

export function buildSparklineDialogUngroupPlan(args: {
  selectedGroupId: string | null | undefined
  editingGroupId: string | null | undefined
}): SparklineDialogCommandPlan {
  const groupId = String(args.selectedGroupId || '').trim()
  if (!groupId) return { action: 'noop' }
  return {
    action: 'ungroup-group',
    groupId,
    clearEditingGroup: args.editingGroupId === groupId,
  }
}
